/**
 * useBrandSwitcher - manages brand selection state for the dashboard.
 */

import { useCallback, useEffect, useState } from 'react'
import type { Brand } from '@provendoro/shared/types'
import * as brandsApi from './api-client.js'

export interface UseBrandSwitcherReturn {
  brands: Brand[]
  selectedBrand: Brand | null
  loading: boolean
  error: string | null
  selectBrand: (brand: Brand) => void
  refreshBrands: () => Promise<void>
}

export function useBrandSwitcher(): UseBrandSwitcherReturn {
  const [brands, setBrands] = useState<Brand[]>([])
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchBrands = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await brandsApi.getBrands()
      if (result.error) {
        setError(result.error.message)
      } else if (result.data) {
        const data = result.data
        setBrands(data)
        // Auto-select first brand if none selected
        setSelectedBrand((prev) => {
          if (!prev && data.length > 0) return data[0]
          return prev
        })
      }
    } catch {
      setError('Failed to load brands')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchBrands()
  }, [fetchBrands])

  const selectBrand = useCallback((brand: Brand) => {
    setSelectedBrand(brand)
  }, [])

  return {
    brands,
    selectedBrand,
    loading,
    error,
    selectBrand,
    refreshBrands: fetchBrands,
  }
}
